import React, { createContext, useState, useContext, useEffect } from 'react';
import { useClerkAuth } from './ClerkAuthContext';

export const UserContext = createContext();

const LIMITS_BY_PLAN = {
  gratuit:    { eventsLimit: 5,        participantsLimit: 20,       datesLimit: 3 },
  pro:        { eventsLimit: Infinity, participantsLimit: 100,      datesLimit: Infinity },
  entreprise: { eventsLimit: Infinity, participantsLimit: Infinity, datesLimit: Infinity },
};

// Default state before the stats fetch resolves. plan=null makes every
// `user.plan === 'gratuit'` guard evaluate false, so the free-tier UI does
// not flash on paid accounts while we wait for /api/user.
const DEFAULT_USER = {
  plan: null,
  eventsThisMonth: 0,
  eventsLimit: Infinity,
  participantsLimit: Infinity,
  datesLimit: Infinity,
  canRemoveBranding: false,
  canExportData: false,
  canCustomize: false,
};

const buildUserFromStats = (stats) => {
  const plan = stats?.plan || 'gratuit';
  const limits = LIMITS_BY_PLAN[plan] || LIMITS_BY_PLAN.gratuit;
  const apiLimit = stats?.quota?.limit; // number | null (null = unlimited)
  return {
    plan,
    eventsThisMonth: stats?.eventsCreatedThisMonth ?? 0,
    eventsLimit: apiLimit === null ? Infinity : (apiLimit ?? limits.eventsLimit),
    participantsLimit: limits.participantsLimit,
    datesLimit: limits.datesLimit,
    canRemoveBranding: !!stats?.canRemoveBranding,
    canExportData: !!stats?.canExportData,
    canCustomize: !!stats?.canCustomizeColors,
  };
};

export const UserProvider = ({ children }) => {
  const { user: clerkUser, isSignedIn } = useClerkAuth();
  const [user, setUser] = useState(DEFAULT_USER);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const email = clerkUser?.primaryEmailAddress?.emailAddress;
    if (!isSignedIn || !email) {
      setUser(DEFAULT_USER);
      setIsLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/user?action=stats&email=${encodeURIComponent(email)}`);
        if (!res.ok) throw new Error(`stats fetch ${res.status}`);
        const stats = await res.json();
        if (!cancelled) setUser(buildUserFromStats(stats));
      } catch (e) {
        console.warn('[UserContext] stats fetch failed, keeping generous defaults:', e);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [clerkUser, isSignedIn]);

  const isLimitReached = (limitType) => {
    switch (limitType) {
      case 'events':
        return user.eventsThisMonth >= user.eventsLimit;
      case 'participants':
      case 'dates':
      default:
        return false;
    }
  };

  const incrementEvents = () => {
    setUser((prev) => ({ ...prev, eventsThisMonth: prev.eventsThisMonth + 1 }));
  };

  return (
    <UserContext.Provider value={{ user, setUser, isLimitReached, incrementEvents, isLoaded }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser doit être utilisé à l\'intérieur d\'un UserProvider');
  }
  return context;
};
